import React, { useState } from 'react';
import { ReactComponent as MasterCardIcon } from 'assets/icons/mastercard-icon.svg';
import { ReactComponent as VisaIcon } from 'assets/icons/visa-icon.svg';
import { ReactComponent as VerveIcon } from 'assets/icons/verve-icon.svg';
import PageHeader from 'components/base/page-header';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import PaymentTable from './payment-table';
import { usePaystackPayment } from 'react-paystack';
import FundWalletModal from 'components/modals/fund-wallet-modal';
import { handleApiError } from 'utils/functions';

const Payment = () => {
  const { activeCompany, paymentCards, refetchPaymentCards, paystackProps } = useDashboardContext();

  const [topupWalletModal, setTopupWalletModal] = useState(false);
  const [setAsPrimaryLoading, setSetAsPrimaryLoading] = useState(false);
  const companyId: string = activeCompany?.id;

  const setCardAsPriamry = async (cardId: string, userId: string) => {
    if (!companyId) return;
    setSetAsPrimaryLoading(true);
    const data = {
      company_id: activeCompany?.id,
      card_id: cardId,
      user_id: userId
    };

    try {
      await apiService.post('billing/card/set/primary', { data });
      refetchPaymentCards();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSetAsPrimaryLoading(false);
    }
  };

  const getCardIcon = (cardName: string) => {
    switch (cardName.toLowerCase().trim()) {
      case 'mastercard':
        return <MasterCardIcon className='mr-4 ml-2' />;
      case 'verve':
        return <VerveIcon />;
      default:
        return <VisaIcon className='mr-3 ml-2' />;
    }
  };

  const initializePayment: any = usePaystackPayment({
    ...paystackProps,
    metadata: {
      ...paystackProps.metadata,
      customer: {
        ...paystackProps.metadata.customer,
        fund_business_wallet: true
      }
    },
    amount: 100 * 100
  } as any);

  const addCard = () => {
    initializePayment((context: any) => {
      if (context.status === 'success') {
        toast(<ToastMessage type='success' message='New card successfully added' />);
        refetchPaymentCards();
      } else toast(<ToastMessage type='error' message='Failed to add a new card' />);
    });
  };

  return (
    <div className='lg:pr-[240px] md:pr-[40px]'>
      <PageHeader
        className='border-b-[4px] border-[#F7F7F8] pb-2'
        title='Payment'
        subtitle='Add or select Bank Cards, and Access Monthly Statements'
      />
      <div className='border-b-[4px] border-[#F7F7F8]'>
        <p className='font-medium text-[20px] mb-2'>Manage billing method(s)</p>
        {paymentCards.length === 0 ? (
          <p className='my-6'>No payment card found</p>
        ) : (
          <div className='flex flex-col mb-6'>
            {paymentCards?.map((card) => (
              <div
                key={card.id}
                className='flex items-center justify-between py-4 border-b border-gray-13'>
                <div className='flex gap-5 items-center'>
                  {getCardIcon(card.card_name)}
                  <div>
                    <p className='font-medium'>Ending in ***{card.last_four_digit}</p>
                    <p className='text-xs font-[#6F7376] font-[400]'>
                      Added {moment(card.created_at).format('MMMM YYYY')}
                    </p>
                  </div>
                </div>
                {card.is_primary ? (
                  <div className='text-[#3FB827] bg-[#EDFBEA] rounded-full py-1 px-3 font-medium text-xs'>
                    Primary
                  </div>
                ) : (
                  <div
                    className='text-[#6F7376] bg-[#F2F2F3] rounded-full py-1 px-3 font-medium text-xs cursor-pointer'
                    onClick={() => setCardAsPriamry(card.id, card.user_id)}>
                    {setAsPrimaryLoading ? 'Setting...' : 'Set as primary'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className='space-y-3 mb-10'>
          <button className='flex items-center text-[#0080E5] font-medium' onClick={addCard}>
            + Add bank card
          </button>
          <button
            className='flex items-center text-[#0080E5] font-medium'
            onClick={() => setTopupWalletModal(true)}>
            + Top up wallet
          </button>
        </div>
      </div>
      <div>
        <PaymentTable />
      </div>
      {/* Top up wallet modal */}
      <FundWalletModal
        isOpen={topupWalletModal}
        onClose={() => setTopupWalletModal((prev) => !prev)}
      />
    </div>
  );
};

export default Payment;
